window.theme = window.theme || {};
window.theme.gwp_status = 'loading';

/**
 * Returns the subset of gift `handles` that exist in the CURRENT store.
 * GWP gifts are configured once for all regions, so a gift can be missing in
 * a market (e.g. SG): probing it with /products/{handle}.js then logs a 404
 * console error on every page view. This asks templates/search.gwp-probe.liquid
 * (/search?view=gwp-probe&q=h1,h2 — always HTTP 200) which checks handles
 * server-side via all_products: the SAME Online-Store visibility as the .js
 * endpoint, so it can never disagree with it. (Storefront GraphQL was wrong
 * here: its app-channel visibility hid genuinely published gifts.)
 *
 * FAIL-OPEN: on any doubt — fetch error, non-200, HTML instead of JSON
 * (probe view not deployed), echo mismatch — the original list is returned
 * unchanged, so worst case is today's behaviour (404 logged, GWP working).
 * Only positive results are cached; max 20 handles per probe (all_products
 * render limit), chunked.
 */
window.theme.gwpExistingHandles = async function (handles) {
  var unique = Array.from(new Set((handles || []).filter(Boolean)));
  if (!unique.length) { return unique; }
  try {
    var known = {};
    var toQuery = [];
    unique.forEach(function (h) {
      var cached = null;
      try { cached = sessionStorage.getItem('gwp_exists_' + String(h).toLowerCase()); } catch (e) { }
      if (cached === '1') { known[String(h).toLowerCase()] = true; }
      else { toQuery.push(h); }
    });

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    for (var i = 0; i < toQuery.length; i += 20) {
      var chunk = toQuery.slice(i, i + 20);
      var res = await fetch(root + 'search?view=gwp-probe&q=' + encodeURIComponent(chunk.join(',')));
      if (!res.ok) { return unique; }
      var json;
      try { json = JSON.parse(await res.text()); } catch (e) { return unique; }
      if (!json || typeof json.q !== 'string' || json.q.indexOf(String(chunk[0])) === -1 || !Array.isArray(json.found)) {
        return unique;
      }
      json.found.forEach(function (h) {
        var key = String(h).toLowerCase();
        known[key] = true;
        try { sessionStorage.setItem('gwp_exists_' + key, '1'); } catch (e) { }
      });
    }
    return unique.filter(function (h) { return known[String(h).toLowerCase()]; });
  } catch (e) {
    return unique;
  }
};

async function buildCompleteGwpObject() {
  cleanExpiredGwpCache();
  const rawEl = document.getElementById('gwp-json');
  if (!rawEl) {
    window.theme.gwp_status = 'loaded';
    return false;
  }
  let raw;
  try {
    raw = JSON.parse(rawEl.textContent);
    // raw = mock_gwp_json;
  } catch (e) {
    console.error('Invalid JSON', e);
    window.theme.gwp_status = 'loaded';
    return false;
  }
  window.theme.giftWithPurchase = {
    enabled: raw.gwpSettings?.enabled || false,
    productsExcluded: (raw.gwpSettings?.productsExcluded || []).map((p) => p.handle),
    multiQualificationEnabled: raw.gwpSettings?.multiQualificationEnabled || false,
    multiEnabled: raw.gwpSettings?.multiEnabled || false,
    heading: raw.gwpSettings?.heading || '',
    description: raw.gwpSettings?.description || '',
    promotionTitle: raw.gwpSettings?.promotionTitle || '',
    addToCart: 'Add to bag',
    offers: [],
  };
  const offers = raw.offers || [];
  const filteredOffers = [];
  for (const offer of offers) {
    if (offer?.discount?.status !== 'ACTIVE') continue;
    const configRaw = offer?.configurationField?.nodes?.[0]?.value;
    const configId = offer?.configurationField?.nodes?.[0]?.id;
    if (!configRaw) continue;
    const config = JSON.parse(configRaw);
    console.log('config', config);
    if (config.enableGWPConfig == false) continue;
    const rawHandles = (config.gwpProducts || []).map((p) => p.handle);
    // Drop gifts that don't exist in this store before fetching them, so a
    // globally-configured gift missing in this region doesn't log a 404.
    // Fail-open: on any probe doubt this returns rawHandles unchanged.
    const handles = window.theme.gwpExistingHandles
      ? await window.theme.gwpExistingHandles(rawHandles)
      : rawHandles;
    //console.log('handles',handles);
    const detailedProducts = await Promise.all(
      handles.map(async (handle) => {
        const key = `gwp_product_${handle}`;
        const cached = sessionStorage.getItem(key);

        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const now = Date.now();

            if (parsed.expiresAt > now && parsed.data && parsed.data.available) {
              return parsed.data;
            } else {
              sessionStorage.removeItem(key); // expired or unavailable
            }
          } catch (err) {
            sessionStorage.removeItem(key); // corrupted
          }
        }
        try {
          const res = await fetch(`/products/${handle}.js`);
          const data = await res.json();
          //console.log('data',handle,data,data.available);
          // return data?.available ? data : null;
          if (data && data.available) {
            sessionStorage.setItem(
              key,
              JSON.stringify({
                data,
                expiresAt: Date.now() + 2 * 60 * 1000, // 2 mins
              })
            );
            //console.log('data',handle,data,data.available);
            return data;
          } else {
            return null;
          }
        } catch (e) {
          console.warn(`Failed to fetch product for handle: ${handle}`);
          return null;
        }
      })
    );
    //console.log('detailedProducts',detailedProducts);
    const filterdProducts = detailedProducts.filter(Boolean);
    //console.log('filterdProducts',filterdProducts);
    if (!filterdProducts.length) continue; // Only consider if we have product.available == true;
    var uniqueProducts = [];
    var seenIds = {};

    for (var i = 0; i < filterdProducts.length; i++) {
      var product = filterdProducts[i];

      if (!seenIds[product.id]) {
        seenIds[product.id] = true;
        uniqueProducts.push(product);
      }
    }
    var expandedProducts = [];

    for (var i = 0; i < uniqueProducts.length; i++) {
      var product = uniqueProducts[i];

      // Product WITH variants
      if (product.variants && product.variants.length) {
        for (var j = 0; j < product.variants.length; j++) {
          var variant = product.variants[j];

          // Shallow clone product (no mutation)
          var productCopy = {};
          for (var key in product) {
            if (product.hasOwnProperty(key)) {
              productCopy[key] = product[key];
            }
          }

          // Keep FULL variant object
          productCopy.variants = [variant];

          // Helpful explicit references (optional but recommended)
          productCopy.selectedVariant = variant;
          productCopy.selectedVariantId = variant.id;

          expandedProducts.push(productCopy);
        }
      }
      // Product WITHOUT variants (edge case)
      else {
        expandedProducts.push(product);
      }
    }
    let blockId = configId.replace('gid://shopify/Metafield/', 'blockId-');
    const offerObj = {
      gwpOfferId: offer?.discount?.discountId,
      heading: config.heading,
      gwpOfferName: config.gwpOfferName,
      autoAddGwp: config.autoAddGwp,
      gwpBlockId: config.gwpBlockId || blockId,
      enableRule1: config.enableRule1,
      gwpProducts: handles,
      maxProducts: Number(config.maxFreeProducts) || 1,
      gwpProductsDetailed: expandedProducts,
    };
    const rules = ['rule1', 'rule2', 'rule3'];
    for (const ruleKey of rules) {
      const rule = config[ruleKey] || {};
      const enabled = config[`enable${ruleKey.charAt(0).toUpperCase() + ruleKey.slice(1)}`];
      let threshold = rule.threshold ? (rule.thresholdType == 'amount' ? rule.threshold * 100 : rule.threshold) : 0;
      let maxThreshold = rule.maxThreshold ? (rule.thresholdType == 'amount' ? rule.maxThreshold * 100 : rule.maxThreshold) : '';

      const shouldInclude = enabled && (threshold > 0 || maxThreshold);
      if (shouldInclude) {
        offerObj[`enable${ruleKey.charAt(0).toUpperCase() + ruleKey.slice(1)}`] = true;
        offerObj[ruleKey] = {
          thresholdType: rule.thresholdType || '',
          threshold: threshold,
          maxThreshold: maxThreshold || '',
          collection: rule.collection?.handle || '',
          collectionProducts: rule.collectionProducts || [],
          tieThresholdToCollection: rule.collection ? true : false,
        };
      } else {
        offerObj[`enable${ruleKey.charAt(0).toUpperCase() + ruleKey.slice(1)}`] = false;
      }
    }
    filteredOffers.push(offerObj);
  }
  if (filteredOffers.length > 1) {
    filteredOffers.forEach((offer) => {
      ['rule1', 'rule2', 'rule3'].forEach((ruleKey) => {
        if (offer[ruleKey]) {
          offer[ruleKey].tieThresholdToCollection = false;
        }
      });
    });
  }
  window.theme.giftWithPurchase.offers = filteredOffers;
  window.theme.gwp_status = 'loaded';
  return window.theme.giftWithPurchase;
}

function cleanExpiredGwpCache() {
  const now = Date.now();
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith('gwp_product_')) {
      try {
        const item = JSON.parse(sessionStorage.getItem(key));
        if (item.expiresAt < now) {
          sessionStorage.removeItem(key);
        }
      } catch {
        sessionStorage.removeItem(key);
      }
    }
  });
}
buildCompleteGwpObject();